import { HttpEvent, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { WafIntegrationService } from '../../services/waf-integration.service';

/**
 * WAM-457 — attaches the AWS WAF JS-integration token to `/public/signup/*`
 * requests only (`nest/cdk/lib/waf.ts`'s `PublicSignupChallenge` rule). Every
 * other request — including the rest of `apiV2` — passes through untouched;
 * this SDK is scoped to the sign-up wizard on purpose (see
 * {@link WafIntegrationService}).
 *
 * `x-aws-waf-token` is the header name the JS integration SDK documents for
 * attaching a token retrieved via `getToken()` on a cross-domain setup (the
 * SPA and the API are on different origins here — see the service doc).
 *
 * Never blocks the request: {@link WafIntegrationService.getToken} always
 * resolves (never errors) within its own timeout, falling back to `null`,
 * in which case the request is sent with no token at all.
 *
 * Registered in `main.ts` alongside `bearerInterceptor`. Order versus that
 * interceptor does not matter — they key off disjoint URL prefixes
 * (`/public/signup/` vs everything `bearerInterceptor` skips is a superset
 * of `/public/`), so neither one's header touches the other's requests.
 */
export const wafTokenInterceptor: HttpInterceptorFn = (req, next): Observable<HttpEvent<unknown>> => {
  const signupPrefix = `${environment.apiV2}/public/signup/`;
  if (!req.url.startsWith(signupPrefix)) return next(req);

  const wafIntegration = inject(WafIntegrationService);
  return wafIntegration.getToken().pipe(
    switchMap((token) =>
      next(token ? req.clone({ setHeaders: { 'x-aws-waf-token': token } }) : req),
    ),
  );
};
